import classNames from 'classnames';

import type { FeedingDashboardOverview } from '@/domain/feedingDashboard';

import { feedWeightQuality } from './components/bioscopeQuality';
import { ClosingDayLabel } from './components/ClosingDayLabel';
import { FallthroughDailyChart } from './components/FallthroughDailyChart';
import { FallthroughPerPenChart } from './components/FallthroughPerPenChart';
import { FeedingKpiRow } from './components/FeedingKpiRow/FeedingKpiRow';
import { FeedWeightChart } from './components/FeedWeightChart';
import { PenComparisonCharts } from './components/PenComparisonCharts';
import { PenComparisonTable } from './components/PenComparisonTable/PenComparisonTable';
import { hasEvaluatedFigures } from './FeedingDashboardNotices';

import styles from './FeedingDashboard.module.scss';

interface FeedingDashboardFiguresProps {
    overview: FeedingDashboardOverview;
    /** Unique label per pen id, from `penDisplayNames` over the period's pens. */
    penNames: ReadonlyMap<number, string>;
    /** The pellet flag and the hardware together; see FeedingDashboardView. */
    pelletShown: boolean;
    /** True while `overview` is the previous selection's response. */
    updating: boolean;
    /** The site's data sources page, linked from the not-mapped caption; null for none. */
    dataSourcesHref?: string | null;
}

/** Everything under the notices: KPI cards, timelines, bar charts and the pen table. */
export function FeedingDashboardFigures(props: FeedingDashboardFiguresProps) {
    const { overview, penNames, pelletShown, updating, dataSourcesHref = null } = props;
    // A period without a complete day still has timelines: today, marked in progress.
    // Only the KPIs, the table and the bars are left out, and the empty state says why.
    const figures = hasEvaluatedFigures(overview);

    return (
        // Without the busy mark, the old figures sit under the new dates in the picker and
        // read as the new period's.
        <div className={classNames(styles.figures, updating && styles.stale)} aria-busy={updating}>
            {figures ? (
                <div className={styles.kpiArea}>
                    <ClosingDayLabel closingDay={overview.closingDay} className={styles.kpiDay} />
                    {/* Four cards without the pellet one, so the grid fills whole rows
                        rather than leaving one card alone. */}
                    <div
                        className={classNames(styles.kpiRow, {
                            [styles.kpiRowFour]: !pelletShown,
                        })}>
                        <FeedingKpiRow
                            summary={overview.summary}
                            config={overview.config}
                            pens={overview.pens}
                            penNames={penNames}
                            closingDay={overview.closingDay}
                            showFallthrough={pelletShown}
                        />
                    </div>
                </div>
            ) : null}

            {/* Each dot reflects the panel's OWN Bioscope data. A site can have weights but
                no RGI (weight plan unmapped) or no pellet data (telemetry stopped), and a
                green dot on those panels would sit beside a blank. This one plots several
                pens at once, today included, so it follows the worst pen in the series it
                draws. */}
            <FeedWeightChart
                series={overview.feedWeightSeries}
                dataSources={overview.dataSources}
                overlaySources={overview.feedWeightOverlaySources}
                resolution={overview.resolution}
                siteToday={overview.siteToday}
                periodStart={overview.from}
                periodEnd={overview.to}
                pmsSystemName={overview.config.pmsSystemName}
                bioscopeDataQuality={feedWeightQuality(overview.feedWeightSeries)}
                dataSourcesHref={dataSourcesHref}
            />

            {figures ? (
                <div className={styles.chartGrid}>
                    <PenComparisonCharts
                        pens={overview.pens}
                        penNames={penNames}
                        config={overview.config}
                        dataSources={overview.dataSources}
                        closingDay={overview.closingDay}
                        bioscopeDataQuality={overview.summary.bioscopeDataQuality}
                    />
                </div>
            ) : null}

            {/* The Pellet column needs the flag and the hardware (Bioscope 3.0, IPS req
                25); pens with no pellet data for the period show a dash, which is a data
                gap rather than a missing capability. */}
            {figures ? (
                <PenComparisonTable
                    pens={overview.pens}
                    penNames={penNames}
                    config={overview.config}
                    closingDay={overview.closingDay}
                    fallthroughSupported={pelletShown}
                />
            ) : null}

            {/* Past the flag, gated on the HARDWARE only (IPS req 27). A 3.0 site whose
                telemetry has stopped keeps both panels, showing an empty state and a grey
                quality dot: an absent panel reads as an unimplemented feature and takes req
                11's indicator with it. */}
            {pelletShown ? (
                <div className={styles.chartGrid}>
                    {/* Both rate their own dot on pellet data, which the weight state says
                        nothing about. */}
                    <FallthroughDailyChart
                        days={overview.fallthroughDaily}
                        config={overview.config}
                        resolution={overview.resolution}
                        siteToday={overview.siteToday}
                        periodStart={overview.from}
                        periodEnd={overview.to}
                    />
                    {figures ? (
                        <FallthroughPerPenChart
                            pens={overview.pens}
                            penNames={penNames}
                            config={overview.config}
                        />
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
